import { useMutation, useQuery } from "@apollo/client";
import { Alert } from "antd";
import React, { FC, useEffect, useMemo, useState } from "react";
import { Checkbox, Form, Icon } from "semantic-ui-react";
import { IInvoice, PaymentProvider } from "../../../types";
import Button from "../../../_components/Button";
import { CustomInput } from "../../../_components/Input";
import { EDIT_INSPECTION_INVOICE } from "../_graphql/mutation";
import { PAYMENT_PROVIDERS } from "../_graphql/query";

import styles from "./styles.module.scss";

export interface EditInspectionInvoiceProps {
  invoice: IInvoice;
  close: Function;
}

export const EditInspectionInvoice: FC<EditInspectionInvoiceProps> = ({
  invoice,
  close,
}) => {
  const [inspectionAmount, setInspectionAmount] = useState<string>(
    invoice.inspectionAmount.toString()
  );

  const [
    allowedPaymentProviderChannels,
    setAllowedPaymentProviderChannels,
  ] = useState<string[]>([]);

  const {
    loading: getPaymentProvidersLoading,
    data: getPaymentProvidersData,
    error: getPaymentProvidersError,
  } = useQuery<{
    paymentProviders: PaymentProvider[];
  }>(PAYMENT_PROVIDERS);

  const paymentProviders = getPaymentProvidersData?.paymentProviders;

  const paymentProvidersGroupedByChannel = paymentProviders?.reduce(
    (acc, curr) => {
      if (!acc[curr.channel]) {
        acc[curr.channel] = [];
      }
      acc[curr.channel].push(curr);
      return acc;
    },
    {} as {
      [key: string]: PaymentProvider[];
    }
  );

  const preSelectedChannels = useMemo(() => {
    /**
     * Initializing a set to make sure channels are unique
     *
     * The allowedPaymentProvider property on the invoice is an array of provider ids
     * We want to get their unique channels
     */
    const preSelectedChannelsSet = new Set<string>();

    if (invoice && paymentProviders) {
      invoice.allowedPaymentProviders.forEach((allowedPaymentProvider) => {
        /**
         * Check if the invoice's allowed payment provider exists in the paymentProviders array
         *
         * If it does, we want to add its channel to the set
         *
         * if it doesn't, then we simply ignore it
         */
        const providerChannel = paymentProviders.find(
          (provider) => provider.id === allowedPaymentProvider
        )?.channel;

        providerChannel && preSelectedChannelsSet.add(providerChannel);
      });
    }

    return Array.from(preSelectedChannelsSet);
  }, [paymentProviders, invoice]);

  useEffect(() => {
    if (paymentProviders && invoice) {
      setAllowedPaymentProviderChannels(preSelectedChannels);
    }
  }, [paymentProviders, invoice]);

  const [editInspectionInvoice, { loading, error }] = useMutation(
    EDIT_INSPECTION_INVOICE,
    {
      onCompleted: () => {
        close();
      },
      onError: () => {},
    }
  );

  const handlePaymentProviderChannelCheckboxChange = (
    channel: string,
    checked?: boolean
  ) => {
    setAllowedPaymentProviderChannels((channels) => {
      if (checked) {
        return [...channels, channel];
      }

      return channels.filter((allowedChannel) => allowedChannel !== channel);
    });
  };

  const getPaymentProviderChannelLabel = (
    channel: string,
    providers: PaymentProvider[]
  ) => {
    return `${channel.toLowerCase()} (${providers
      .map(({ name }) => name)
      .join(", ")})`;
  };

  const handleSubmit = () => {
    editInspectionInvoice({
      variables: {
        invoiceId: invoice.id,
        amount: Number(inspectionAmount),
        ...(paymentProvidersGroupedByChannel && {
          allowedPaymentProviders: allowedPaymentProviderChannels
            .flatMap((channel) => paymentProvidersGroupedByChannel[channel])
            .map(({ id }) => id),
        }),
      },
    });
  };

  return (
    <>
      {error && <Alert banner type='error' message={error.message}></Alert>}
      <h4 className={styles.heading}>Edit Inspection Invoice</h4>
      <div className=''>
        <div className='flex flex-col flex-nowrap gap-2'>
          <Form>
            <CustomInput
              textLabel='Set Amount'
              type='number'
              value={inspectionAmount}
              onChange={(value) => {
                setInspectionAmount(value);
              }}
            />
          </Form>

          <div className='my-4 flex flex-col gap-2'>
            <h5 className='text-primary-500 font-semibold'>
              Set Payment Providers
            </h5>
            <div className='flex flex-col gap-1'>
              {getPaymentProvidersLoading ? (
                <Icon name='spinner' loading />
              ) : (
                Object.entries(paymentProvidersGroupedByChannel || {}).map(
                  ([channel, providers]) => {
                    return (
                      <Checkbox
                        className='mb-2 capitalize'
                        label={getPaymentProviderChannelLabel(
                          channel,
                          providers
                        )}
                        checked={allowedPaymentProviderChannels.includes(
                          channel
                        )}
                        onChange={(e, { checked }) =>
                          handlePaymentProviderChannelCheckboxChange(
                            channel,
                            checked
                          )
                        }
                      />
                    );
                  }
                )
              )}
            </div>
          </div>
        </div>

        <div className='flex justify-end'>
          <Button
            loading={loading}
            className={styles.button}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </div>
      </div>
    </>
  );
};
